import { httpClient } from '../utils/httpClient';
import Logger from '../utils/logger';
import get from 'lodash/get';
import {
  IMAGE_PROMPT_GENERATE,
  IMAGE_GENERATE,
  IMAGE_PROMPT_OPTIONS,
  IMAGE_PROMPT_GENERATE_V2,
  IMAGE_LOCALES,
  IMAGE_CONFIG,
  IMAGE_GENERATE_V2,
  IMAGE_STATUS,
  IMAGE_CANCEL,
} from '../constants/apiRoutes';
import {
  PromptOptionsResponse,
  LocalesResponse,
  LocaleOption,
  ImageConfigResponse,
  PromptGenerationRequestV2,
  PromptGenerationResponseV2,
  ImageGenerationRequestV2,
  ImageGenerationResponseV2,
  ImageStatusResponse,
  CancelGenerationResponse,
} from '../types/imageGeneration';

// API Response Types
interface PromptGenerationResponse {
  success: boolean;
  message: string;
  data?: {
    prompt?: string;
  };
}

interface ImageGenerationResponse {
  success: boolean;
  message: string;
  statusCode?: number;
  status?: string;
  timestamp?: string;
  errors?: string[];
  data?: {
    fireflyResponse?: string;
    imageJobId?: string;
  };
}

// API Payload Types
interface PromptGenerationPayload {
  story: string;
  subject: string;
  style: string;
  purpose: string;
}

interface ImageGenerationPayload {
  prompt: string;
}

/**
 * Generates a prompt based on user inputs
 */
export const generatePrompt = async (
  payload: PromptGenerationPayload
): Promise<PromptGenerationResponse> => {
  try {
    console.log(
      '🚀 PROMPT GENERATION REQUEST:',
      JSON.stringify(payload, null, 2)
    );

    const response = await httpClient.post<PromptGenerationResponse>(
      IMAGE_PROMPT_GENERATE,
      payload
    );

    // Enhanced debugging for response structure
    console.log('🔍 FULL RESPONSE OBJECT:', JSON.stringify(response, null, 2));
    console.log('🔍 RESPONSE TYPE:', typeof response);
    console.log(
      '🔍 RESPONSE KEYS:',
      response && typeof response === 'object'
        ? Object.keys(response)
        : 'Not an object'
    );

    // Log individual response properties
    if ((response as any).data) {
      console.log(
        '🔍 response.data:',
        JSON.stringify((response as any).data, null, 2)
      );
    }
    if ((response as any).body) {
      console.log(
        '🔍 response.body:',
        JSON.stringify((response as any).body, null, 2)
      );
    }

    // Enhanced response structure handling with priority order
    let extractedData = null;
    let dataSource = '';

    // Priority 1: Check if response has success and data properties directly (MSW format)
    if (
      response &&
      (response as any).success !== undefined &&
      (response as any).data
    ) {
      extractedData = (response as any).data;
      dataSource = 'direct response (MSW format)';
      console.log(
        '✅ Found data in direct response (MSW format):',
        extractedData
      );
    }
    // Priority 2: Check response.data with success property
    else if (
      (response as any).data &&
      (response as any).data.success !== undefined
    ) {
      extractedData = (response as any).data.data || (response as any).data;
      dataSource = 'response.data with success';
      console.log(
        '✅ Found data in response.data with success:',
        extractedData
      );
    }
    // Priority 3: Check response.body (Axios format)
    else if ((response as any).body && (response as any).body.data) {
      extractedData = (response as any).body.data;
      dataSource = 'response.body.data';
      console.log('✅ Found data in response.body.data:', extractedData);
    }
    // Priority 4: Check response.data.data (nested format)
    else if ((response as any).data && (response as any).data.data) {
      extractedData = (response as any).data.data;
      dataSource = 'response.data.data';
      console.log('✅ Found data in response.data.data:', extractedData);
    }
    // Priority 5: Check response.data (simple format)
    else if ((response as any).data) {
      extractedData = (response as any).data;
      dataSource = 'response.data';
      console.log('✅ Found data in response.data:', extractedData);
    }
    // Priority 6: Check response.body (alternative format)
    else if ((response as any).body) {
      extractedData = (response as any).body;
      dataSource = 'response.body';
      console.log('✅ Found data in response.body:', extractedData);
    }
    // Priority 7: Use direct response as fallback
    else {
      extractedData = response;
      dataSource = 'direct response';
      console.log('✅ Using direct response:', extractedData);
    }

    console.log(`📍 DATA SOURCE: ${dataSource}`);
    console.log('🔍 EXTRACTED DATA:', JSON.stringify(extractedData, null, 2));

    // Check for prompt in the extracted data
    if (extractedData && extractedData.prompt) {
      console.log('✅ SUCCESS - PROMPT FOUND:', extractedData.prompt);
      console.log('✅ PROMPT LENGTH:', extractedData.prompt.length);
      return {
        success: true,
        data: extractedData,
        message: 'Prompt generated successfully',
      };
    }

    // Additional fallback: check if response itself has prompt property
    if ((response as any).prompt) {
      console.log(
        '✅ SUCCESS - PROMPT FOUND IN DIRECT RESPONSE:',
        (response as any).prompt
      );
      return {
        success: true,
        data: { prompt: (response as any).prompt },
        message: 'Prompt generated successfully',
      };
    }

    console.log('❌ NO PROMPT FOUND IN ANY STRUCTURE');
    console.log(
      '❌ AVAILABLE PROPERTIES IN EXTRACTED DATA:',
      extractedData ? Object.keys(extractedData) : 'No extracted data'
    );

    return {
      success: false,
      message: 'Failed to generate prompt - no prompt property found',
    };
  } catch (error) {
    console.error('❌ PROMPT GENERATION ERROR:', error);
    Logger.error(
      new Error(
        get(
          error,
          'response.body.message',
          get(error, 'response.data.message', 'Error generating prompt')
        )
      )
    );

    return {
      success: false,
      message:
        get(
          error,
          'response.body.message',
          get(error, 'response.data.message', 'Error generating prompt')
        ) || 'An error occurred',
    };
  }
};

/**
 * Generates an image from a prompt
 */
export const generateImage = async (
  payload: ImageGenerationPayload
): Promise<ImageGenerationResponse> => {
  try {
    const response = await httpClient.post<ImageGenerationResponse>(
      IMAGE_GENERATE,
      payload
    );

    // Handle rate limit response (200 OK but with success: false)
    // Check response.body, response.data, and direct response for rate limit info
    const responseData =
      (response as any).body || (response as any).data || response;
    if (responseData && (responseData as any).success === false) {
      return {
        success: false,
        message: (responseData as any).message || 'Request failed',
        statusCode: (responseData as any).statusCode,
        status: (responseData as any).status,
        timestamp: (responseData as any).timestamp,
        errors: (responseData as any).errors,
      };
    }

    const { data } = (
      (response as any).body
        ? { data: (response as any).body }
        : (response as any).data
          ? response
          : { data: response }
    ) as ImageGenerationResponse;

    if (data) {
      return {
        success: true,
        data,
        message: 'Image generated successfully',
      };
    }

    return {
      success: false,
      message: 'Failed to generate image',
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error generating image'))
    );

    const errorResponse = get(error, 'response.data', {});

    return {
      success: false,
      message:
        get(error, 'response.data.message', 'Error generating image') ||
        'An error occurred',
      statusCode:
        get(error, 'response.status') || get(errorResponse, 'statusCode'),
      status: get(errorResponse, 'status'),
      timestamp: get(errorResponse, 'timestamp'),
      errors: get(errorResponse, 'errors'),
    };
  }
};

// =============================================================================
// V2 API Functions (New content-agent APIs)
// =============================================================================

/**
 * Helper to extract response data from httpClient response
 * Handles various response formats (MSW, Axios, nested)
 */
const extractResponseData = <T>(response: unknown): T => {
  const res = response as Record<string, unknown>;

  // Priority 1: Direct response with success property (MSW format)
  if (res?.success !== undefined && res?.data) {
    return res.data as T;
  }
  // Priority 2: response.data with nested data
  if (res?.data && typeof res.data === 'object') {
    const dataObj = res.data as Record<string, unknown>;
    if (dataObj?.data) {
      return dataObj.data as T;
    }
    return res.data as T;
  }
  // Priority 3: response.body
  if (res?.body) {
    const bodyObj = res.body as Record<string, unknown>;
    if (bodyObj?.data) {
      return bodyObj.data as T;
    }
    return res.body as T;
  }
  // Fallback
  return response as T;
};

/**
 * Sort locales with US at top, rest alphabetically
 */
const sortLocales = (locales: LocaleOption[]): LocaleOption[] => {
  const usLocale = locales.find(l => l.code === 'en-US');
  const others = locales
    .filter(l => l.code !== 'en-US')
    .sort((a, b) => a.label.localeCompare(b.label));
  return usLocale ? [usLocale, ...others] : others;
};

/**
 * Fetches dynamic prompt options (story, subject with types, style)
 * GET /api/content-agent/v1/image/prompt-options
 */
export const fetchPromptOptions = async (): Promise<PromptOptionsResponse> => {
  try {
    const response =
      await httpClient.get<PromptOptionsResponse>(IMAGE_PROMPT_OPTIONS);
    const data = extractResponseData<PromptOptionsResponse['data']>(response);

    if (data) {
      return { success: true, data };
    }

    return {
      success: false,
      data: { story: [], subject: [], style: [] },
    };
  } catch (error) {
    Logger.error(
      new Error(
        get(error, 'response.data.message', 'Error fetching prompt options')
      )
    );
    return {
      success: false,
      data: { story: [], subject: [], style: [] },
    };
  }
};

/**
 * Fetches available locales
 * GET /api/content-agent/v1/image/locales
 * Returns sorted with US at top
 */
export const fetchLocales = async (): Promise<LocalesResponse> => {
  try {
    const response = await httpClient.get<LocalesResponse>(IMAGE_LOCALES);
    const data = extractResponseData<LocalesResponse['data']>(response);

    if (data?.locales) {
      return {
        success: true,
        data: {
          locales: sortLocales(data.locales),
        },
      };
    }

    return {
      success: false,
      data: { locales: [] },
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error fetching locales'))
    );
    return {
      success: false,
      data: { locales: [] },
    };
  }
};

/**
 * Fetches image generation configuration (sizes, contentClasses, defaults)
 * GET /api/content-agent/v1/image/config
 */
export const fetchImageConfig = async (): Promise<ImageConfigResponse> => {
  try {
    const response = await httpClient.get<ImageConfigResponse>(IMAGE_CONFIG);
    const data = extractResponseData<ImageConfigResponse['data']>(response);

    if (data) {
      return { success: true, data };
    }

    return {
      success: false,
      data: {
        sizes: [],
        contentClasses: [],
        variations: { min: 1, max: 4, default: 1 },
        defaults: { size: '2048x2048', contentClass: 'photo', locale: 'en-US' },
        limits: { maxPromptLength: 1024 },
      },
    };
  } catch (error) {
    Logger.error(
      new Error(
        get(error, 'response.data.message', 'Error fetching image config')
      )
    );
    return {
      success: false,
      data: {
        sizes: [],
        contentClasses: [],
        variations: { min: 1, max: 4, default: 1 },
        defaults: { size: '2048x2048', contentClass: 'photo', locale: 'en-US' },
        limits: { maxPromptLength: 1024 },
      },
    };
  }
};

/**
 * Generates a prompt (V2 API with sessionId)
 * POST /api/content-agent/v1/image/prompt/generate
 */
export const generatePromptV2 = async (
  payload: PromptGenerationRequestV2
): Promise<PromptGenerationResponseV2> => {
  try {
    const response = await httpClient.post<PromptGenerationResponseV2>(
      IMAGE_PROMPT_GENERATE_V2,
      payload
    );

    // Handle various response formats
    const res = response as Record<string, unknown>;

    // Check if httpClient returned an error response (e.g., 500 error)
    // httpClient returns { success: false, status: <http_status>, ... } for errors
    if (res?.success === false) {
      const errorMessage =
        (res?.message as string) || 'Failed to generate prompt';
      return {
        success: false,
        message: errorMessage,
      };
    }

    // Check for direct success response
    if (res?.success && res?.data) {
      const data = res.data as PromptGenerationResponseV2['data'];
      return { success: true, data };
    }

    // Check nested data
    const data =
      extractResponseData<PromptGenerationResponseV2['data']>(response);
    if (data?.prompt && data?.sessionId && data?.promptRequestId) {
      return { success: true, data };
    }

    return {
      success: false,
      message: 'Failed to generate prompt',
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error generating prompt'))
    );
    return {
      success: false,
      message:
        get(error, 'response.data.message', 'Error generating prompt') ||
        'An error occurred',
    };
  }
};

/**
 * Generates images (V2 API - returns contentId for polling)
 * POST /api/content-agent/v1/image/generate
 */
export const generateImageV2 = async (
  payload: ImageGenerationRequestV2
): Promise<ImageGenerationResponseV2> => {
  try {
    const response = await httpClient.post<ImageGenerationResponseV2>(
      IMAGE_GENERATE_V2,
      payload
    );

    const res = response as Record<string, unknown>;

    // Check if httpClient returned an error response (e.g., 500 error)
    // httpClient returns { success: false, status: <http_status>, ... } for errors
    if (res?.success === false) {
      const errorMessage =
        (res?.message as string) || 'Failed to start image generation';
      return {
        success: false,
        message: errorMessage,
      };
    }

    // Check for direct success response
    if (res?.success && res?.data) {
      const data = res.data as ImageGenerationResponseV2['data'];
      return { success: true, data };
    }

    const data =
      extractResponseData<ImageGenerationResponseV2['data']>(response);
    if (data?.contentId) {
      return { success: true, data };
    }

    return {
      success: false,
      message: 'Failed to start image generation',
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error generating image'))
    );
    return {
      success: false,
      message:
        get(error, 'response.data.message', 'Error generating image') ||
        'An error occurred',
    };
  }
};

/**
 * Gets image generation status (for polling)
 * GET /api/content-agent/v1/image?sessionId=...&contentId=...&promptRequestId=...
 */
export const getImageStatus = async (
  sessionId: string,
  contentId: string,
  promptRequestId: string
): Promise<ImageStatusResponse> => {
  try {
    const response = await httpClient.get<ImageStatusResponse>(
      IMAGE_STATUS(sessionId, contentId, promptRequestId)
    );

    const res = response as Record<string, unknown>;

    // Check if httpClient returned an error response (e.g., 500 error)
    // httpClient returns { success: false, status: <http_status>, ... } for errors
    if (res?.success === false) {
      const errorMessage =
        (res?.message as string) || 'Failed to get image status';
      return {
        success: false,
        data: {
          contentId,
          rootContentId: contentId,
          parentId: null,
          versionNumber: 1,
          status: 'failed',
          error: {
            code: 'API_ERROR',
            message: errorMessage,
          },
        },
      };
    }

    // Check for direct success response
    if (res?.success && res?.data) {
      return { success: true, data: res.data as ImageStatusResponse['data'] };
    }

    const data = extractResponseData<ImageStatusResponse['data']>(response);
    // Ensure we have a valid image generation status (not HTTP status code)
    const validStatuses = [
      'idle',
      'queued',
      'processing',
      'completed',
      'failed',
      'canceled',
    ];
    if (data?.status && validStatuses.includes(data.status as string)) {
      return { success: true, data };
    }

    return {
      success: false,
      data: {
        contentId,
        rootContentId: contentId,
        parentId: null,
        versionNumber: 1,
        status: 'failed',
        error: { code: 'UNKNOWN', message: 'Failed to get status' },
      },
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error getting status'))
    );
    return {
      success: false,
      data: {
        contentId,
        rootContentId: contentId,
        parentId: null,
        versionNumber: 1,
        status: 'failed',
        error: {
          code: 'NETWORK_ERROR',
          message:
            get(error, 'response.data.message', 'Error getting status') ||
            'Network error',
        },
      },
    };
  }
};

/**
 * Cancels image generation
 * POST /api/content-agent/v1/image/cancel
 */
export const cancelImageGeneration = async (
  sessionId: string,
  contentId: string,
  promptRequestId: string
): Promise<CancelGenerationResponse> => {
  try {
    const response = await httpClient.post<CancelGenerationResponse>(
      IMAGE_CANCEL,
      { sessionId, contentId, promptRequestId }
    );

    const res = response as Record<string, unknown>;

    // Check for direct success response
    if (res?.success !== undefined) {
      return response as CancelGenerationResponse;
    }

    const data = extractResponseData<CancelGenerationResponse>(response);
    return data;
  } catch (error) {
    Logger.error(
      new Error(
        get(error, 'response.data.message', 'Error canceling generation')
      )
    );

    // Check if it's an "already processed" or "already completed" error
    const errorData = get(error, 'response.data', {}) as Record<
      string,
      unknown
    >;

    return {
      success: false,
      error: {
        code:
          ((errorData?.error as Record<string, unknown>)?.code as string) ||
          'CANCEL_ERROR',
        message:
          ((errorData?.error as Record<string, unknown>)?.message as string) ||
          get(error, 'response.data.message', 'Error canceling generation') ||
          'An error occurred',
        userAction:
          ((errorData?.error as Record<string, unknown>)
            ?.userAction as string) || 'Please try again.',
      },
    };
  }
};
